import React from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { fieldError } from "../../principalFormUtils";
import SelectedAttorneyInfoCard from "./SelectedAttorneyInfoCard";
import { getAttorneyAutocompleteNoOptionsText } from "../../utils/attorneySearch";
import {
  buildBondAttorneySearchInputHandler,
  buildBondGuardianAttorneySearchHelperText,
} from "../../utils/bondAttorneySearchWiring";

function getAttorneyHomeAddressLabel(attorneyUi) {
  if (attorneyUi === "guardianship") return "Step 2: Enter Home Address:";
  if (attorneyUi === "probate") return "Enter Home Address:";
  return null;
}

function getAttorneyOfficeAddressLabel(attorneyUi) {
  if (attorneyUi === "va") return "Address";
  return "Office Address";
}

/** Attorney path: autocomplete, office card, optional home address. */
export default function PrincipalAttorneySection({
  cfg,
  index: i,
  guardian,
  guardians,
  data,
  updateData,
  errors = {},
  keys,
  gVal,
  attorneys,
  allCities,
  allStates,
  loadingAttorneys,
  loadingCitiesStates,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  handleGoogleCallback,
  handleZipCodeChange,
  handleAttorneySelection,
  handleAttorneyHomeAddressChange,
  attorneySearchEnabled = false,
  attorneySearchInput = "",
  setAttorneySearchInput,
  debouncedSearchAttorneys,
}) {
  const attorneyCardTitle =
    cfg.attorneyUi === "guardianship" || cfg.attorneyUi === "probate"
      ? "Selected Attorney Details:"
      : "Selected Attorney:";

  const useServerSearch = attorneySearchEnabled && debouncedSearchAttorneys;

  const attorneyInputChangeHandler =
    useServerSearch && setAttorneySearchInput
      ? buildBondAttorneySearchInputHandler(
          setAttorneySearchInput,
          debouncedSearchAttorneys
        )
      : undefined;

  const renderAttorneyAutocomplete = (extraTextFieldProps = {}) => {
    const fieldErr = extraTextFieldProps.helperText || fieldError(errors, i, "attorney");
    const searchHelperText = useServerSearch
      ? buildBondGuardianAttorneySearchHelperText(
          attorneySearchInput,
          loadingAttorneys,
          attorneys.length,
          fieldErr
        )
      : fieldErr;

    return (
      <Autocomplete
        options={attorneys}
        value={attorneys.find((a) => a.value === guardian.attorney) || null}
        getOptionLabel={(option) => option.label || ""}
        filterOptions={
          useServerSearch
            ? (options) => options
            : (options, { inputValue }) => {
                if (!inputValue || inputValue.length < 3) return [];
                const input = inputValue.toLowerCase();
                return options.filter((opt) =>
                  (opt.label || "").toLowerCase().includes(input)
                );
              }
        }
        noOptionsText={
          useServerSearch
            ? getAttorneyAutocompleteNoOptionsText(attorneySearchInput, loadingAttorneys)
            : "Type at least 3 characters to search for attorneys"
        }
        loading={loadingAttorneys}
        onInputChange={attorneyInputChangeHandler}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Attorney"
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingAttorneys ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
            {...extraTextFieldProps}
            helperText={searchHelperText}
            error={extraTextFieldProps.error || !!fieldError(errors, i, "attorney")}
          />
        )}
        onChange={(event, newValue) => handleAttorneySelection(i, newValue)}
        {...(cfg.attorneyUi === "probate" ? { isClearable: true } : {})}
      />
    );
  };

  const renderAttorneyHomeFields = (useErrors) => (
    <Box className="grid grid-cols-1 gap-4" sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
        {getAttorneyHomeAddressLabel(cfg.attorneyUi)}
      </Typography>
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(0, 0, 0, 0.6)",
            marginBottom: "4px",
            fontWeight: 500,
          }}
        >
          Search Address
        </Typography>
        <GoogleAutoComplete
          placeholder="Search Home Address"
          callback={(address, city, state, zipCode) =>
            handleGoogleCallback(i, address, city, state, zipCode)
          }
        />
      </Box>
      <TextField
        sx={{ mt: 1, mb: 1 }}
        label="Street Address"
        value={gVal(guardian, keys, "address")}
        onChange={(e) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = {
            ...updatedGuardians[i],
            [keys.address]: e.target.value,
          };
          updateData({ ...data, guardians: updatedGuardians });
        }}
        fullWidth
        required
        {...(useErrors
          ? {
              error: !!fieldError(errors, i, keys.address),
              helperText: fieldError(errors, i, keys.address),
            }
          : {})}
      />
      <TextField
        label="ZIP/Postal Code"
        value={gVal(guardian, keys, "zip")}
        onChange={(e) => handleZipCodeChange(e, i)}
        fullWidth
        required
        {...(useErrors
          ? {
              error: !!fieldError(errors, i, keys.zip),
              helperText: fieldError(errors, i, keys.zip),
            }
          : {})}
      />
      {loadingCitiesStates ? (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <LabeledCreatableSelect
            label="City"
            options={allCities.map((city) => ({ label: city, value: city }))}
            value={
              gVal(guardian, keys, "city")
                ? {
                    label: gVal(guardian, keys, "city"),
                    value: gVal(guardian, keys, "city"),
                  }
                : null
            }
            onChange={(opt) => {
              const updatedGuardians = [...guardians];
              updatedGuardians[i] = {
                ...updatedGuardians[i],
                [keys.city]: opt ? opt.value : "",
              };
              updateData({ ...data, guardians: updatedGuardians });
            }}
            placeholder="Select or add a city"
          />
          <LabeledCreatableSelect
            label="State"
            options={allStates.map((state) => ({ label: state, value: state }))}
            value={
              gVal(guardian, keys, "state")
                ? {
                    label: gVal(guardian, keys, "state"),
                    value: gVal(guardian, keys, "state"),
                  }
                : null
            }
            onChange={(opt) => {
              const updatedGuardians = [...guardians];
              updatedGuardians[i] = {
                ...updatedGuardians[i],
                [keys.state]: opt ? opt.value : "",
              };
              updateData({ ...data, guardians: updatedGuardians });
            }}
            placeholder="Select or add a state"
          />
        </>
      )}
    </Box>
  );

  if (cfg.attorneyUi === "probate") {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            Step 1: Select Attorney
          </Typography>
          {renderAttorneyAutocomplete({
            required: true,
            error: !!fieldError(errors, i, "attorney"),
            helperText: fieldError(errors, i, "attorney"),
          })}
        </Box>
        {guardian.attorney ? (
          <>
            <SelectedAttorneyInfoCard
              title={attorneyCardTitle}
              attorney={attorneys.find((a) => a.value === guardian.attorney)}
              officeAddressLabel="Office Address"
            />
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                Step 2: Address Information
              </Typography>
              <TextField
                select
                label="Use attorney's home address?"
                value={guardian.is_attorney_home || "No"}
                onChange={(e) => handleAttorneyHomeAddressChange(i, e.target.value)}
                fullWidth
                required
                error={!!fieldError(errors, i, "is_attorney_home")}
                helperText={fieldError(errors, i, "is_attorney_home")}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Box>
            {guardian.is_attorney_home === "Yes" && renderAttorneyHomeFields(true)}
          </>
        ) : null}
      </Box>
    );
  }

  if (cfg.attorneyUi === "guardianship" || cfg.attorneyUi === "trustee") {
    const homeStepLabel =
      cfg.attorneyUi === "guardianship"
        ? "Step 1: Address Information"
        : "Step 2: Address Information";
    const useErrors = cfg.attorneyUi === "guardianship";
    const attorneyHomeFieldProps = useErrors
      ? {
          error: !!fieldError(errors, i, "is_attorney_home"),
          helperText: fieldError(errors, i, "is_attorney_home"),
        }
      : {};

    return (
      <Box sx={{ mt: 2 }}>
        {renderAttorneyAutocomplete()}
        {guardian.is_attorney === "Yes" &&
          guardian.attorney &&
          (() => {
            const attorney = attorneys.find((a) => a.value === guardian.attorney);
            return attorney ? (
              <>
                <SelectedAttorneyInfoCard
                  title={attorneyCardTitle}
                  attorney={attorney}
                  officeAddressLabel="Office Address"
                />
                <Box sx={{ mb: 3, mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    {homeStepLabel}
                  </Typography>
                  <TextField
                    select
                    label="Use attorney's home address?"
                    value={guardian.is_attorney_home || "No"}
                    onChange={(e) => handleAttorneyHomeAddressChange(i, e.target.value)}
                    fullWidth
                    required
                    {...attorneyHomeFieldProps}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Box>
                {guardian.is_attorney_home === "Yes" && renderAttorneyHomeFields(useErrors)}
              </>
            ) : null;
          })()}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {renderAttorneyAutocomplete()}
      {guardian.is_attorney === "Yes" &&
        guardian.attorney &&
        (() => {
          const attorney = attorneys.find((a) => a.value === guardian.attorney);
          return attorney ? (
            <SelectedAttorneyInfoCard
              title={attorneyCardTitle}
              attorney={attorney}
              officeAddressLabel={getAttorneyOfficeAddressLabel(cfg.attorneyUi)}
            />
          ) : null;
        })()}
    </Box>
  );
}

PrincipalAttorneySection.propTypes = {
  cfg: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  keys: PropTypes.object.isRequired,
  gVal: PropTypes.func.isRequired,
  attorneys: PropTypes.array.isRequired,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  loadingAttorneys: PropTypes.bool.isRequired,
  loadingCitiesStates: PropTypes.bool.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
  handleAttorneySelection: PropTypes.func.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
  attorneySearchEnabled: PropTypes.bool,
  attorneySearchInput: PropTypes.string,
  setAttorneySearchInput: PropTypes.func,
  debouncedSearchAttorneys: PropTypes.func,
};
